/**
 * Analytics event allowlist — the single source of truth BOTH the web client
 * (apps/web/src/lib/track.ts) and the API server (POST /api/activity/events)
 * trust when recording a product-analytics event.
 *
 * Batch 3 of the July 2026 design handoff adds a theme picker whose purpose is
 * conversion. These events measure that funnel:
 *   - theme_previewed {theme, tier} — user hovered/opened a theme in the picker
 *   - theme_applied   {theme}       — user committed to a theme
 *   - upgrade_clicked {from}        — user clicked an upgrade CTA
 *   - accent_set      {hue}         — paid user committed a custom accent hue
 *
 * Batch 5 adds the in-app help center (9 short videos). These events measure
 * which guides get watched and whether they land:
 *   - help_video_played    {id, from}     — user opened a help video
 *   - help_video_completed {id}           — user watched a help video to the end
 *   - hint_opened          {id, screen}   — user tapped a contextual hint dot
 *   - help_feedback        {id, helpful}  — user answered "was this helpful?"
 *
 * Batch 6 adds sharing + referrals. These events measure the share funnel and
 * referral attribution:
 *   - share_opened            {type}                          — user opened the share sheet
 *   - share_completed         {type, format, question_included} — user rendered/shared a card
 *   - share_link_visited      {shortid}                       — a public /r/:shortid link was opened
 *   - referral_signup         {code}                          — a signup carried a referral code
 *   - referral_activated      {code}                          — a referral converted to Cosmic
 *   - practitioner_attribution {deck}                         — a share/referral credited a deck creator
 *
 * Both the event NAME and the PROP SHAPE are allowlist-validated server-side
 * (see validateActivityEvent). Unknown events, missing props, extra props, or
 * wrong-typed prop values are rejected (400) rather than silently stored — the
 * activity_events log stays clean and the funnel numbers stay trustworthy.
 */
import { isThemeId } from "./themes";
import { isHelpVideoId } from "./help";
import {
  isShareType,
  isShareFormat,
  SHARE_SHORTID_MAX_LEN,
  REFERRAL_CODE_MAX_LEN,
  ATTRIBUTION_DECK_MAX_LEN,
} from "./sharing";

export const ACTIVITY_EVENTS = [
  "theme_previewed",
  "theme_applied",
  "upgrade_clicked",
  "accent_set",
  "help_video_played",
  "help_video_completed",
  "hint_opened",
  "help_feedback",
  "share_opened",
  "share_completed",
  "share_link_visited",
  "referral_signup",
  "referral_activated",
  "practitioner_attribution",
] as const;

export type ActivityEventName = (typeof ACTIVITY_EVENTS)[number];

const ACTIVITY_EVENT_SET = new Set<string>(ACTIVITY_EVENTS);

/** Type guard: is `value` one of the registered analytics event names? */
export function isActivityEventName(value: unknown): value is ActivityEventName {
  return typeof value === "string" && ACTIVITY_EVENT_SET.has(value);
}

type PropValidator = (value: unknown) => boolean;

/** Bounded non-empty string validator. Caps length so an authed client can't
 *  bloat activity_events.meta with megabyte prop values. */
const isBoundedString =
  (max: number): PropValidator =>
  (value: unknown): value is string =>
    typeof value === "string" && value.length > 0 && value.length <= max;

/** Max length for the free-form `tier` prop (values are 'free' / 'paid'). */
const MAX_TIER_LEN = 32;

/** Max length for the free-form `screen` prop on `hint_opened` — the name of
 *  the app screen the hint dot lives on. Bounded so it can't bloat meta. */
const MAX_SCREEN_LEN = 48;

/** Strict boolean validator (rejects truthy/falsy non-booleans like 0 / "true"
 *  / null). `help_feedback.helpful` must be a real boolean. */
const isStrictBoolean: PropValidator = (value: unknown): boolean =>
  typeof value === "boolean";

/** Custom-accent hue validator: an integer degree in [0, 359]. Rejects null,
 *  floats, out-of-range numbers, and non-numbers — the client only ever fires
 *  `accent_set` for a committed (non-null) hue, so null here is invalid. */
const isAccentHue: PropValidator = (value: unknown): boolean =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= 0 &&
  value <= 359;

/**
 * Per-event required prop schema. Every listed key is REQUIRED and validated;
 * a payload with a missing key, an extra key, or an invalid value is rejected.
 */
const EVENT_PROP_SCHEMAS: Record<
  ActivityEventName,
  Record<string, PropValidator>
> = {
  theme_previewed: { theme: isThemeId, tier: isBoundedString(MAX_TIER_LEN) },
  theme_applied: { theme: isThemeId },
  upgrade_clicked: {
    from: (value) => value === "appearance" || value === "preview_bar",
  },
  accent_set: { hue: isAccentHue },
  help_video_played: {
    id: isHelpVideoId,
    from: (value) => value === "list" || value === "hint" || value === "onboarding",
  },
  help_video_completed: { id: isHelpVideoId },
  hint_opened: { id: isHelpVideoId, screen: isBoundedString(MAX_SCREEN_LEN) },
  help_feedback: { id: isHelpVideoId, helpful: isStrictBoolean },
  // Batch 6 — sharing + referrals.
  share_opened: { type: isShareType },
  share_completed: {
    type: isShareType,
    format: isShareFormat,
    question_included: isStrictBoolean,
  },
  share_link_visited: { shortid: isBoundedString(SHARE_SHORTID_MAX_LEN) },
  referral_signup: { code: isBoundedString(REFERRAL_CODE_MAX_LEN) },
  referral_activated: { code: isBoundedString(REFERRAL_CODE_MAX_LEN) },
  practitioner_attribution: { deck: isBoundedString(ATTRIBUTION_DECK_MAX_LEN) },
};

export type ActivityEventValidation =
  | { ok: true; event: ActivityEventName; props: Record<string, unknown> }
  | { ok: false; error: string };

/**
 * Allowlist-validate an incoming analytics event. Returns the narrowed event +
 * props on success, or a human-readable reason on failure. Callers should map a
 * failure to HTTP 400.
 */
export function validateActivityEvent(
  event: unknown,
  props: unknown,
): ActivityEventValidation {
  if (!isActivityEventName(event)) {
    return { ok: false, error: "unknown event" };
  }
  if (props === null || typeof props !== "object" || Array.isArray(props)) {
    return { ok: false, error: "props must be an object" };
  }

  const schema = EVENT_PROP_SCHEMAS[event];
  const propObj = props as Record<string, unknown>;

  for (const key of Object.keys(propObj)) {
    // Own-property membership only: `in` walks the prototype chain, so
    // JSON.parse-produced own keys like "__proto__" / "constructor" / "toString"
    // would pass a plain `key in schema` guard and get persisted into meta.
    if (!Object.prototype.hasOwnProperty.call(schema, key)) {
      return { ok: false, error: `unknown prop: ${key}` };
    }
  }
  for (const [key, validate] of Object.entries(schema)) {
    if (!Object.prototype.hasOwnProperty.call(propObj, key)) {
      return { ok: false, error: `missing prop: ${key}` };
    }
    if (!validate(propObj[key])) {
      return { ok: false, error: `invalid prop: ${key}` };
    }
  }

  return { ok: true, event, props: propObj };
}
